import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, filter, map, mergeMap, switchMap } from "rxjs/operators";
import { AbstractCirculationsService } from "src/app/interfaces";
import { Notification } from "src/app/notifications/notification";
import { NotificationActions } from "src/app/notifications/state/actions";
import { NavigationService } from "src/app/services";
import { CirculationAPIActions, CirculationDocumentActions } from "./actions";

@Injectable()
export class CirculationEffects {
  constructor(
    private actions$: Actions,
    private service: AbstractCirculationsService,
    private navigate: NavigationService
  ) {}

  saveCirculation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CirculationAPIActions.saveCirculation),
      mergeMap((action) =>
        this.service.SaveCirculation(action.circulation).pipe(
          map((id) => CirculationAPIActions.saveCirculationSuccess({ id })),
          catchError((error) =>
            of(CirculationAPIActions.saveCirculationFailure({ error }))
          )
        )
      )
    );
  });

  copyCirculation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CirculationAPIActions.copyCirculation),
      mergeMap((action) =>
        this.service.CopyCirculation(action.circId).pipe(
          map((id) => CirculationAPIActions.copyCirculationSuccess({ id })),
          catchError((error) =>
            of(CirculationAPIActions.copyCirculationFailure({ error }))
          )
        )
      )
    );
  });

  /* eslint-disable */
  //rxjs/no-cyclic-action
  removeCirculationDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CirculationDocumentActions.removeDocument),
      mergeMap((action) =>
        this.service.RemoveDocument(action.id, action.docId).pipe(
          map(() =>
            CirculationDocumentActions.removeDocumentSuccess({
              id: action.id,
              docId: action.docId
            })
          ),
          catchError((error) =>
            of(
              CirculationDocumentActions.removeDocumentFailed({
                id: action.id,
                docId: action.docId,
                error: error
              })
            )
          )
        )
      )
    );
  });
  /* eslint-enable */
  loadCirculations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CirculationAPIActions.loadCirculations),
      switchMap(() =>
        this.service.GetCirculations(false).pipe(
          map((circulations) =>
            CirculationAPIActions.loadCirculationsSuccess({ circulations })
          ),
          catchError((error: HttpErrorResponse) => {
            return of(CirculationAPIActions.loadCirculationsFailure({ error }));
          })
        )
      )
    );
  });
  loadCirculationsFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CirculationAPIActions.loadCirculationsFailure),
      map((x) => {
        if (x.error.status === 404) {
          this.navigate.toError("circ.load.not_found");
          return;
        }

        if (x.error.status === 403) {
          this.navigate.toError("circ.load.not_allowed");
          return;
        }

        const notification = {
          content: "LOADCIRCULATIONFAILURE_MESSAGE",
          level: "error"
        } as Notification;
        return NotificationActions.showNotification({ notification });
      }),
      filter((x) => !!x)
    );
  });

  saveCirculationSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CirculationAPIActions.saveCirculationSuccess),
      switchMap((action) =>
        of(CirculationAPIActions.loadCirculation({ id: action.id }))
      )
    );
  });

  copyCirculationSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CirculationAPIActions.copyCirculationSuccess),
      switchMap((action) =>
        of(CirculationAPIActions.loadCirculation({ id: action.id }))
      )
    );
  });
  loadCirculation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CirculationAPIActions.loadCirculation),
      // It is possible that many Ids are queried at the same time
      mergeMap((action) =>
        this.service.GetCirculation(action.id).pipe(
          map((circulation) =>
            CirculationAPIActions.loadCirculationSuccess({ circulation })
          ),
          catchError((error) =>
            of(CirculationAPIActions.loadCirculationFailure({ error }))
          )
        )
      )
    );
  });
  loadCirculationFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CirculationAPIActions.loadCirculationFailure),
      map((x) => {
        if (x.error.status === 404) {
          this.navigate.toError("circ.load.not_found");
          return;
        }

        if (x.error.status === 403) {
          this.navigate.toError("circ.load.not_allowed");
          return;
        }

        const notification = {
          content: "LOADCIRCULATIONFAILURE_MESSAGE",
          level: "error"
        } as Notification;
        return NotificationActions.showNotification({ notification });
      }),
      filter((x) => !!x)
    );
  });
  uploadDocumentFailed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CirculationDocumentActions.uploadDocumentFailed),
      map(() => {
        const notification = {
          content: "UPLOADDOCUMENTFAILED_MESSAGE",
          level: "error"
        } as Notification;
        return NotificationActions.showNotification({ notification });
      })
    );
  });
}
